import React, { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Chip, Tooltip, IconButton } from '@mui/material';
import { CloudOff, CloudSync, CloudDone, Sync } from '@mui/icons-material';
import syncService from '../../services/SyncService';

/**
 * Componente que monitorea el estado de sincronización offline
 * y muestra notificaciones al usuario
 */
const SyncStatusWatcher = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [queueLength, setQueueLength] = React.useState(syncService.getQueue().length);
    const [isSyncing, setIsSyncing] = React.useState(syncService.isSyncing);

    useEffect(() => {
        const handleQueued = (event) => {
            setQueueLength(syncService.getQueue().length);
            enqueueSnackbar(`Offline: ${event.detail.actionName} guardado localmente`, {
                variant: 'info',
                persist: false,
                icon: <CloudOff sx={{ mr: 1 }} />
            });
        };

        const handleSynced = (event) => {
            setQueueLength(syncService.getQueue().length);
            enqueueSnackbar(`Sincronizado: ${event.detail.actionName}`, {
                variant: 'success',
                autoHideDuration: 3000
            });
        };

        const handleSyncCompleted = () => {
            setQueueLength(0);
            setIsSyncing(false);
            enqueueSnackbar('Todos los datos han sido sincronizados con el servidor', {
                variant: 'success',
                autoHideDuration: 5000
            });
        };

        window.addEventListener('offline-action-queued', handleQueued);
        window.addEventListener('offline-action-synced', handleSynced);
        window.addEventListener('sync-completed', handleSyncCompleted);

        // Verificar estado cada 5 segundos para la UI
        const interval = setInterval(() => {
            setQueueLength(syncService.getQueue().length);
            setIsSyncing(syncService.isSyncing);
        }, 5000);

        return () => {
            window.removeEventListener('offline-action-queued', handleQueued);
            window.removeEventListener('offline-action-synced', handleSynced);
            window.removeEventListener('sync-completed', handleSyncCompleted);
            clearInterval(interval);
        };
    }, [enqueueSnackbar]);

    if (queueLength === 0 && !isSyncing) return null;

    return (
        <Box sx={{ position: 'fixed', bottom: 20, left: 20, zIndex: 2000 }}>
            <Tooltip title={isSyncing ? "Sincronizando con el servidor..." : `${queueLength} operaciones pendientes de sincronizar`}>
                <Chip
                    icon={isSyncing ? <Sync className="fa-spin" /> : <CloudOff />}
                    label={isSyncing ? "Sincronizando..." : `${queueLength} pendiente(s)`}
                    color={isSyncing ? "primary" : "warning"}
                    variant="filled"
                    onClick={() => syncService.sync()}
                    sx={{
                        boxShadow: 3,
                        cursor: 'pointer',
                        '& .fa-spin': {
                            animation: 'spin 2s linear infinite',
                        },
                        '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                        }
                    }}
                />
            </Tooltip>
        </Box>
    );
};

export default SyncStatusWatcher;
